import Clipboard from '@react-native-clipboard/clipboard';
import { Redirect, router } from 'expo-router';
import { useAtomValue, useSetAtom } from 'jotai';
import * as React from 'react';
import { useState } from 'react';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    ClipboardIcon,
    FootnoteRegular,
    Icon,
    InfoIcon,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import PopUpReceipt from '../../../common/components/PopUpReceipt';
import { getAccountHolders } from '../../../common/utils/bankingUtils';
import LogRocketFacade from '../../../logger/log-rocket/LogRocketFacade';
import { captureMessage } from '../../../logger/Logger';
import { editingSpaceAtom, selectedSpaceAtom } from '../../state/SpacesAtoms';
import { RelationshipStatus } from '../../types/BankAccount';

const LegacyAccountDetailsModal = () => {
    LogRocketFacade.tagPage('AccountDetailsModal');

    const selectedSpace = useAtomValue(selectedSpaceAtom);
    const setEditingSpace = useSetAtom(editingSpaceAtom);

    if (selectedSpace == null) {
        captureMessage('No space selected to display account details modal');
        return <Redirect href="/home/banking" />;
    }

    const {
        availableToWithdraw,
        accountNumber,
        routingNumber,
        primaryUser,
        jointUser,
        jointUserRelationshipStatus,
    } = selectedSpace;

    return (
        <PopUpReceipt
            title="Account details"
            additionalButtons={[
                <Button
                    key="edit"
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.LARGE}
                    text="Edit"
                    onPress={() => {
                        setEditingSpace(selectedSpace);
                        router.replace('/banking/create-edit-space');
                    }}
                    buttonProps={{
                        backgroundColor: 'white',
                    }}
                />,
            ]}
        >
            <YStack marginBottom={16}>
                <AccountDetailRow
                    label="Available balance"
                    value={availableToWithdraw}
                    info="Your available balance reflects funds that are currently available to withdraw"
                />
                <AccountDetailRow
                    label="Account number"
                    value={accountNumber}
                    allowCopy
                />
                <AccountDetailRow
                    label="Routing number"
                    value={routingNumber}
                    allowCopy
                />
                <AccountDetailRow
                    label="Account holders"
                    value={getAccountHolders(
                        primaryUser,
                        jointUserRelationshipStatus ===
                            RelationshipStatus.ACTIVE
                            ? jointUser
                            : null,
                    )}
                />
            </YStack>
        </PopUpReceipt>
    );
};

interface AccountDetailRowProps {
    label: string;
    value: string;
    allowCopy?: boolean;
    info?: string;
}

const AccountDetailRow = ({
    label,
    value,
    allowCopy,
    info,
}: AccountDetailRowProps) => {
    const toast = useToastController();

    const [showInfo, setShowInfo] = useState(false);

    return (
        <YStack
            gap={8}
            paddingVertical={16}
            borderBottomWidth={1}
            borderBottomColor="$gray100"
            onPress={() => {
                if (allowCopy) {
                    Clipboard.setString(value);
                    toast.show('Copied!', { duration: 1000 });
                }
            }}
        >
            <XStack alignItems="flex-start" justifyContent="space-between">
                <XStack
                    alignItems="center"
                    gap={8}
                    onPress={() => {
                        setShowInfo(!showInfo);
                    }}
                >
                    <BodyMediumRegular color="$gray900">
                        {label}
                    </BodyMediumRegular>
                    {info != null && (
                        <Icon icon={InfoIcon} size={14} color="$gray900" />
                    )}
                </XStack>
                <XStack alignItems="center" gap={8}>
                    <BodyMediumEmphasized
                        numberOfLines={2}
                        maxWidth={200}
                        textAlign="right"
                    >
                        {value}
                    </BodyMediumEmphasized>
                    {allowCopy && <Icon icon={ClipboardIcon} size={14} />}
                </XStack>
            </XStack>
            {showInfo && (
                <FootnoteRegular color="$gray900">{info}</FootnoteRegular>
            )}
        </YStack>
    );
};

export default LegacyAccountDetailsModal;
